import _ from 'lodash';
import {getSchemaOfComp} from './compSchemas';
import {generateError} from './errors';
import reporter from '../../privates/reporter';
import regexValidations from './regexValidations';
import {isExternalUrl} from '../../privates/urlUtils';

const TYPES = {
    NUMBER: 'number',
    STRING: 'string',
    BOOLEAN: 'boolean',
    DATE: 'date',
    FUNCTION: 'function',
    ARRAY: 'array',
    OBJECT: 'object',
    URL: 'url',
    INTEGER: 'integer',
    UUID: 'uuid'
};

function validateArrayContainsItemsWithIds({items}) {
    let res = true;

    if (!_.isArray(items)) {
        return false;
    }

    const idCounts = _.countBy(items, '_id');

    _.forEach(items, (item) => {
       if (!item._id) {
           reporter.reportSdkError(generateError.error_mandatory_id());
           res = false;
       }

       if (idCounts[item._id] !== 1) {
           reporter.reportSdkWarning(generateError.warning_duplicate_item({functionName: 'data', _id: item._id}));
       }
    });

    return res;
}

function reportHttpUrls({value, propertyName, functionName}) {
    const isHttpSiteReg = /(http):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
    if (isHttpSiteReg.test(value)) {
        reporter.reportSdkWarning(generateError.warning_http_format({
            propertyName,
            functionName
        }));
    }
}

function validateByType({value, expectedType}) {
    switch (expectedType) {
        case TYPES.NUMBER:
            return _.isNumber(value) && !_.isNaN(value);
        case TYPES.STRING:
            return _.isString(value);
        case TYPES.BOOLEAN:
            return _.isBoolean(value);
        case TYPES.DATE:
            return _.isDate(value) && !_.isNaN(value.getTime());
        case TYPES.FUNCTION:
            return _.isFunction(value);
        case TYPES.ARRAY:
            return _.isArray(value);
        case TYPES.OBJECT:
            return _.isObject(value) && !_.isArray(value) && !_.isFunction(value);
        case TYPES.URL:
            return isExternalUrl(value);
        case TYPES.INTEGER:
            return _.isInteger(value);
        case TYPES.UUID:
            return regexValidations.validateUUIDFormat(value);
        default:
            return true;
    }
}

function validateMandatoryFieldsAndReportIfNeeded({compType, functionName, dataToValidate, itemIndex}) {
    let validationRes = {isValid: true};
    const expectedSchema = getSchemaOfComp(compType);
    if (!expectedSchema) {
        return validationRes;
    }
    const mandatoryFields = _.pickBy(expectedSchema, {mandatory: true});
    _.forEach(mandatoryFields, (v, k) => {
        if (!_.has(dataToValidate, k)) {
            validationRes = {isValid: false, prop: k};
            return;
        }
    });

    if (!validationRes.isValid) {
        const error = !_.isNil(itemIndex) ? generateError.error_mandatory_val_with_index : generateError.error_mandatory_val;
        reporter.reportSdkError(error({
            propertyName: validationRes.prop,
            functionName,
            index: itemIndex
        }));
    }

    return validationRes.isValid;
}

function validateLength({functionName, propertyName, value, minLength, maxLength, itemIndex, acceptNil}) {
    const valid = isValueInRange({
        value: _.get(value, 'length'),
        minValue: minLength,
        maxValue: maxLength,
        acceptNil
    });
    if(!valid){
        reporter.reportSdkError(getLengthError({minLength, maxLength, itemIndex})({
            functionName,
            propertyName,
            wrongValue: value,
            minLength,
            maxLength,
            acceptedLength: maxLength,
            index: itemIndex
        }));
    }
    return valid;
}

function getLengthError({minLength, maxLength, itemIndex}) {
    let error = isActualNumber(itemIndex) ? generateError.error_length_in_range_with_index : generateError.error_length_in_range;
    if(!isActualNumber(minLength) || !isActualNumber(maxLength)){
        return error;
    }

    if (minLength === maxLength) {
        error = isActualNumber(itemIndex) ? generateError.error_length_accept_single_value_with_index : generateError.error_length_accept_single_value;
    } else if (minLength === 0 && maxLength > 0) {
        error = isActualNumber(itemIndex) ? generateError.error_length_exceeds_with_index : generateError.error_length_exceeds;
    }
    return error;
}

function validateMinimum({functionName, propertyName, value, minValue, acceptNil}) {
    if (acceptNil && _.isNil(value)) {
        return true;
    }
    if (!isActualNumber(value) || !isActualNumber(minValue) || value < minValue) {
        reporter.reportSdkError(generateError.error_larger_than({
            functionName,
            propertyName,
            wrongValue: value,
            minValue
        }));
        return false;
    }
    return true;
}

function validateMaximum({functionName, propertyName, value, maxValue, acceptNil}) {
    if (acceptNil && _.isNil(value)) {
        return true;
    }
    if (!isActualNumber(value) || !isActualNumber(maxValue) || maxValue < value) {
        reporter.reportSdkError(generateError.error_less_than({
            functionName,
            propertyName,
            wrongValue: value,
            maxValue
        }));
        return false;
    }
    return true;
}

function validateRange({functionName, propertyName, value, minValue, maxValue, itemIndex, acceptNil}) {
    const valid = isValueInRange({value, minValue, maxValue, acceptNil});
    if(!valid){
        reporter.reportSdkError(getRangeError({minValue, maxValue, itemIndex})({
            functionName,
            propertyName,
            wrongValue: value,
            minValue,
            maxValue,
            acceptedValue: minValue,
            index: itemIndex
        }));
    }
    return valid;
}

function isValueInPattern({value, pattern, acceptNil}) {
    if (acceptNil && _.isNil(value)) {
        return true;
    }

    if (typeof value === 'string' && value.match(pattern)) {
        return true;
    }

    return false;
}
function isValueInRange({value, minValue, maxValue, acceptNil}) {
    if (acceptNil && _.isNil(value)) {
        return true;
    }
    if (!isActualNumber(maxValue) || !isActualNumber(value) || !isActualNumber(minValue) ||
        maxValue < value || value < minValue) {
        return false;
    }
    return true;
}

function isActualNumber(value) {
    return _.isNumber(value) && !_.isNaN(value);
}

function getRangeError({minValue, maxValue, itemIndex}) {
    let error = isActualNumber(itemIndex) ? generateError.error_range_with_index : generateError.error_range;
    if(!isActualNumber(minValue) || !isActualNumber(maxValue) || minValue < maxValue){
        return error;
    } else if (minValue === maxValue) {
        error = isActualNumber(itemIndex) ? generateError.error_accept_single_value_with_index : generateError.error_accept_single_value;
    } else if (minValue > maxValue) {
        error = isActualNumber(itemIndex) ? generateError.error_empty_component_with_index : generateError.error_empty_component;
    }
    return error;
}

function getNullWarningTemplate({itemIndex, compName}) {
    if (!_.isNil(itemIndex)) {
        return generateError.warning_not_null_with_index;
    }

    if (compName) {
        return generateError.warning_not_null_for_comp_name;
    }

    return generateError.warning_not_null;
}

function validateType({compType, functionName, propertyName, value, expectedTypes, warnIfNil, acceptNil, shouldCheckMandatoryFields, itemIndex, compName}) {
    if (_.isNil(value)) {
        if (warnIfNil) {
            const error = getNullWarningTemplate({ itemIndex, compName });
            reporter.reportSdkWarning(error({
                propertyName,
                functionName,
                index: itemIndex,
                compName,
                // convert null/undefined to string, since lodash templates ignore null/undefined values
                invalidValue: String(value)
            }));
        }
        if (acceptNil) {
            return true;
        }
    }

    let isValidByType = false;
    let actualType;
    expectedTypes.forEach((expectedType) => {
        if (validateByType({
            value,
            expectedType
        })) {
            isValidByType = true;
            actualType = expectedType;
        }
    });
    if (!isValidByType) {
        const error = !_.isNil(itemIndex) ? generateError.error_type_with_index : generateError.error_type;
        reporter.reportSdkError(error({
            functionName,
            propertyName,
            wrongValue: value,
            expectedType: expectedTypes.join(','),
            index: itemIndex
        }));
        return false;
    }

    if (actualType === 'url' && _.includes(expectedTypes, 'url')) {
        reportHttpUrls({value, propertyName, functionName});
    }

    if (actualType === 'object' && _.includes(expectedTypes, 'object')) {
        const newObjType = _.isArray(compType) ? compType.concat([propertyName]) : [compType, propertyName];
        return validate({
            compType: newObjType,
            functionName,
            dataToValidate: value,
            shouldCheckMandatoryFields,
            itemIndex,
            compName
        });
    }

    return true;
}

function validateValueFromEnum({functionName, propertyName, value, enumValues, itemIndex, acceptNil}) {
    if (_.isNil(value) && acceptNil) {
        return true;
    }
    if (!_.includes(enumValues, value)) {
        const error = !_.isNil(itemIndex) ? generateError.error_type_with_index : generateError.error_type;
        reporter.reportSdkError(error({
            functionName,
            propertyName,
            wrongValue: value,
            expectedType: `from (${enumValues.join(',')})`,
            index: itemIndex
        }));
        return false;
    }
    return true;
}

function validateRegexForValue({functionName, propertyName, value, validationExpressions}) {
    if (!regexValidations[validationExpressions](value)) {
        reporter.reportSdkError(generateError.error_bad_format({
            functionName,
            propertyName,
            wrongValue: value
        }));
        return false;
    }
    return true;
}

function validateRegex({functionName, propertyName, value, validationExpressions}) {
    if (regexValidations[validationExpressions]) {
        if (_.isArray(value)) {
            return _.forEach(value, (val) => validateRegexForValue({functionName, propertyName, value: val, validationExpressions}));
        }
        return validateRegexForValue({functionName, propertyName, value, validationExpressions});
    }
    return true;
}

function validate({compType, functionName, dataToValidate, shouldCheckMandatoryFields, itemIndex, compName}) {
    let res = true;
    const expectedSchema = getSchemaOfComp(compType);
    if (!expectedSchema) {
        return true;
    }
    if (shouldCheckMandatoryFields) {
        if (!validateMandatoryFieldsAndReportIfNeeded({compType, functionName, dataToValidate, itemIndex})) {
            return false;
        }
    }
    _.forEach(dataToValidate, (value, propertyName) => {
        if (!res) {
            return;
        }
        const expectedSchema = getSchemaOfComp(compType)[propertyName];
        if (!expectedSchema) {
            res = true;
            return;
        }
        const validations = expectedSchema.validations || {};

        if (validations.type) {
            if (!validateType({
                compType,
                functionName,
                propertyName,
                value,
                expectedTypes: expectedSchema.schemaType,
                warnIfNil: expectedSchema.warnIfNil,
                acceptNil: expectedSchema.acceptNil,
                shouldCheckMandatoryFields,
                itemIndex,
                compName
            })) {
                res = false;
                return;
            }
            if (expectedSchema.enum && !validateValueFromEnum({
                functionName,
                propertyName,
                value,
                enumValues: expectedSchema.enum,
                itemIndex,
                acceptNil: expectedSchema.acceptNil
            })) {
                res = false;
                return;
            }
        }
        if (validations.range) {
            if (!validateRange({
                functionName,
                propertyName,
                value,
                minValue: expectedSchema.minVal,
                maxValue: expectedSchema.maxVal,
                itemIndex,
                acceptNil: expectedSchema.acceptNil
            })) {
                res = false;
                return;
            }
        }
        if (validations.length) {
            if (!validateLength({
                functionName,
                propertyName,
                value,
                minLength: expectedSchema.minLength,
                maxLength: expectedSchema.maxLength,
                itemIndex,
                acceptNil: expectedSchema.acceptNil
            })) {
                res = false;
                return;
            }
        }
        if (validations.regex) {
            if (!validateRegex({functionName, propertyName, value, validationExpressions: expectedSchema.validationExpressions})) {
                res = false;
                return;
            }
        }

        if (validations.minimum) {
            if (!validateMinimum({
                functionName,
                propertyName,
                value,
                minValue: expectedSchema.minVal,
                acceptNil: expectedSchema.acceptNil
            })) {
                res = false;
                return;
            }
        }

        if (validations.maximum) {
            if (!validateMaximum({
                functionName,
                propertyName,
                value,
                maxValue: expectedSchema.maxVal,
                acceptNil: expectedSchema.acceptNil
            })) {
                res = false;
                return;
            }
        }
    });
    return res;

}

export {
    TYPES,
    validate,
    validateRange,
    validateMinimum,
    validateMaximum,
    validateValueFromEnum,
    validateByType,
    validateArrayContainsItemsWithIds,
    isValueInRange,
    isValueInPattern
};
